import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type {
  CreateRole,
  RoleWithPermissions,
  SetRolePermissions,
  UpdateRole,
} from '@dcc-erp/shared';

import { api, apiJson } from '../api';

const key = ['roles'] as const;

export function useRoles() {
  return useQuery({ queryKey: key, queryFn: () => api<RoleWithPermissions[]>(`/roles`) });
}

export function useCreateRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateRole) =>
      apiJson<CreateRole, RoleWithPermissions>('POST', `/roles`, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: key }),
  });
}

export function useUpdateRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateRole }) =>
      apiJson<UpdateRole, RoleWithPermissions>('PATCH', `/roles/${id}`, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: key }),
  });
}

export function useDeleteRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiJson<undefined, { ok: boolean }>('DELETE', `/roles/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: key }),
  });
}

export function useSetRolePermissions() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: SetRolePermissions }) =>
      apiJson<SetRolePermissions, unknown>('PUT', `/roles/${id}/permissions`, data),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: key });
      void qc.invalidateQueries({ queryKey: ['me'] });
    },
  });
}
